import { decodeBase64UrlUtf8, encodeBase64 } from './base64.js';
import { RtcError, normalizeHttpFailure, rtcError } from './errors.js';
import { systemClock } from './types.js';
/** Resolve the platform fetch lazily so constructing a client never touches the network. */
export function resolveFetch(fetchImpl) {
    if (fetchImpl)
        return fetchImpl;
    return async (url, init) => {
        const candidate = globalThis.fetch;
        if (typeof candidate !== 'function') {
            throw rtcError('RTC_PROVIDER_UNAVAILABLE', 'no fetch implementation is available in this environment');
        }
        const fn = candidate;
        return fn(url, init);
    };
}
/** Best-effort timing extraction from the token payload (the token stays opaque to the provider). */
export function parseTokenTiming(token) {
    const parts = token.split('.');
    if (parts.length !== 3 || parts[0] !== 'nrt1')
        return { expiresAt: null, privilegeExpireAt: null };
    try {
        const payload = JSON.parse(decodeBase64UrlUtf8(parts[1] ?? ''));
        const exp = typeof payload.exp === 'number' ? payload.exp : null;
        const pexp = typeof payload.pexp === 'number' ? payload.pexp : null;
        return {
            expiresAt: exp === null ? null : exp * 1000,
            privilegeExpireAt: pexp === null ? null : pexp * 1000,
        };
    }
    catch {
        return { expiresAt: null, privilegeExpireAt: null };
    }
}
function readRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : null;
}
function readString(record, ...keys) {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'string' && value.length > 0)
            return value;
    }
    return undefined;
}
function readNumber(record, ...keys) {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'number' && Number.isFinite(value))
            return value;
        if (typeof value === 'string' && value.trim().length > 0) {
            const parsed = Number(value);
            if (Number.isFinite(parsed))
                return parsed;
        }
    }
    return undefined;
}
function readRole(record) {
    const role = readString(record, 'role');
    return role === 'host' || role === 'audience' ? role : undefined;
}
function normalizeTokenInfo(raw, fallback = {}) {
    const outer = readRecord(raw);
    if (!outer)
        throw rtcError('invalid_token', 'token provider returned no token object');
    const record = readRecord(outer.data) ?? readRecord(outer.tokenInfo) ?? outer;
    const appId = readString(record, 'appId', 'appid') ?? fallback.appId;
    if (!appId)
        throw rtcError('invalid_appid', 'token provider returned no appId');
    const rtcToken = readString(record, 'rtcToken', 'token') ?? fallback.rtcToken;
    if (!rtcToken)
        throw rtcError('invalid_token', 'token provider returned no rtcToken');
    const rtcUid = readNumber(record, 'rtcUid', 'uid') ?? fallback.rtcUid;
    if (rtcUid === undefined || !Number.isInteger(rtcUid) || rtcUid < 1 || rtcUid > 2147483647) {
        throw rtcError('invalid_token', 'token provider returned no valid rtcUid');
    }
    const expireAt = readString(record, 'expireAt') ?? fallback.expireAt;
    const privilegeExpireAt = readString(record, 'privilegeExpireAt') ?? fallback.privilegeExpireAt;
    const timing = parseTokenTiming(rtcToken);
    const role = readRole(record) ?? fallback.role;
    const channelName = readString(record, 'channelName') ?? fallback.channelName;
    const userId = readString(record, 'userId') ?? fallback.userId;
    const serverUrl = readString(record, 'serverUrl', 'url') ?? fallback.serverUrl;
    const info = { appId, rtcToken, rtcUid };
    if (role)
        info.role = role;
    if (channelName)
        info.channelName = channelName;
    if (userId)
        info.userId = userId;
    if (serverUrl)
        info.serverUrl = serverUrl;
    const expiresAt = expireAt ?? (timing.expiresAt !== null ? new Date(timing.expiresAt).toISOString() : undefined);
    const privilegeAt = privilegeExpireAt ??
        (timing.privilegeExpireAt !== null
            ? new Date(timing.privilegeExpireAt).toISOString()
            : undefined);
    if (expiresAt)
        info.expireAt = expiresAt;
    if (privilegeAt)
        info.privilegeExpireAt = privilegeAt;
    const privileges = record.privileges;
    if (Array.isArray(privileges)) {
        info.privileges = privileges.filter((entry) => typeof entry === 'string');
    }
    return info;
}
function tokenRequestFromToken(current, request) {
    const resolved = request ?? {
        channelName: current.channelName ?? '',
        ...(current.userId !== undefined ? { userId: current.userId } : {}),
        ...(current.role !== undefined ? { role: current.role } : {}),
        uid: current.rtcUid,
    };
    return resolved;
}
function joinUrl(baseUrl, path) {
    return `${baseUrl.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}
function authHeaders(config) {
    if (config.authorization)
        return { authorization: config.authorization };
    if (config.customerId && config.customerSecret) {
        return {
            authorization: `Basic ${encodeBase64(`${config.customerId}:${config.customerSecret}`)}`,
        };
    }
    return {};
}
async function requestJson(options) {
    const { fetchImpl, url, method, headers, body, timeoutMs, clock, label } = options;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timedOut = false;
    const timer = controller !== null && timeoutMs > 0
        ? clock.setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, timeoutMs)
        : null;
    try {
        const response = await fetchImpl(url, {
            method,
            headers,
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
            ...(controller ? { signal: controller.signal } : {}),
        });
        const text = await response.text();
        let parsed = null;
        if (text.length > 0) {
            try {
                parsed = JSON.parse(text);
            }
            catch {
                parsed = text;
            }
        }
        if (!response.ok) {
            throw normalizeHttpFailure(response.status, parsed, `${label} failed with HTTP ${response.status}`);
        }
        return parsed;
    }
    catch (error) {
        if (error instanceof RtcError)
            throw error;
        const detail = error instanceof Error ? error.message : String(error);
        if (timedOut)
            throw rtcError('RTC_PROVIDER_UNAVAILABLE', `${label} timed out after ${timeoutMs}ms`, {
                cause: error,
            });
        throw rtcError('RTC_PROVIDER_UNAVAILABLE', `${label} request failed: ${detail}`, {
            cause: error,
        });
    }
    finally {
        if (timer !== null)
            clock.clearTimeout(timer);
    }
}
function flattenTokenProviders(input) {
    const output = [];
    const seen = new Set();
    const visit = (config, depth) => {
        if (depth > 4 || seen.has(config))
            return;
        seen.add(config);
        output.push(config);
        for (const fallback of config.fallbacks ?? [])
            visit(fallback, depth + 1);
    };
    const list = Array.isArray(input) ? input : [input];
    for (const config of list)
        visit(config, 0);
    return output;
}
function flattenUidMappings(input) {
    const output = [];
    const seen = new Set();
    const visit = (config, depth) => {
        if (depth > 4 || seen.has(config))
            return;
        seen.add(config);
        output.push(config);
        for (const fallback of config.fallbacks ?? [])
            visit(fallback, depth + 1);
    };
    if (!input)
        return output;
    const list = Array.isArray(input) ? input : [input];
    for (const config of list)
        visit(config, 0);
    return output;
}
function asProviderError(error, label) {
    if (error instanceof RtcError)
        return error;
    const message = error instanceof Error ? error.message : String(error);
    return rtcError('RTC_PROVIDER_UNAVAILABLE', `${label} failed: ${message}`, { cause: error });
}
/**
 * Resolves media credentials and uid mappings with ordered fallbacks.
 * Token and uid-mapping chains are independent, exactly like the IM contract requires.
 */
export class RtcTokenProvider {
    tokenProviders;
    uidMappings;
    clock;
    fetchImpl;
    constructor(config, options = {}) {
        this.tokenProviders = flattenTokenProviders(config);
        if (this.tokenProviders.length === 0) {
            throw rtcError('invalid_parameter', 'at least one token provider is required');
        }
        this.uidMappings = flattenUidMappings(options.uidMapping);
        this.clock = options.clock ?? systemClock;
        this.fetchImpl = resolveFetch(options.fetch);
    }
    /** `contracts/rtc-provider.json#getRTCTokenInfo` equivalent. */
    async getRTCTokenInfo(request) {
        if (!request.channelName)
            throw rtcError('invalid_channel_name', 'channelName is required');
        let lastError = null;
        for (let index = 0; index < this.tokenProviders.length; index += 1) {
            const config = this.tokenProviders[index];
            if (!config)
                continue;
            try {
                const info = await this.resolveWith(config, request);
                return { ...info, providerType: config.type, usedFallback: index > 0 };
            }
            catch (error) {
                lastError = asProviderError(error, `${config.type} token provider`);
            }
        }
        throw (lastError ??
            rtcError('RTC_PROVIDER_UNAVAILABLE', 'no token provider could issue a credential'));
    }
    /**
     * Renew an existing credential. Uses the control-plane renew endpoint when an HTTP provider is
     * configured, otherwise re-resolves through the normal chain (callback/static hosts).
     */
    async renewToken(current, request) {
        const effectiveRequest = tokenRequestFromToken(current, request);
        const endpoint = this.tokenProviders.find((config) => config.type === 'endpoint' && typeof config.baseUrl === 'string' && !config.fetchToken);
        if (endpoint) {
            try {
                const raw = await requestJson({
                    fetchImpl: endpoint.fetch ?? this.fetchImpl,
                    url: joinUrl(endpoint.baseUrl, endpoint.renewPath ?? '/v1/tokens/renew'),
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        accept: 'application/json',
                        ...authHeaders(endpoint),
                    },
                    body: { token: current.rtcToken },
                    timeoutMs: endpoint.timeoutMs ?? 2000,
                    clock: this.clock,
                    label: 'token renewal',
                });
                const info = normalizeTokenInfo(raw, current);
                return { ...info, providerType: 'endpoint', usedFallback: false };
            }
            catch (error) {
                const renewalError = asProviderError(error, 'token renewal');
                // A renew endpoint that rejects a token must not hide the reason: try the chain next, but
                // surface both failures if re-resolution also fails.
                try {
                    return await this.getRTCTokenInfo(effectiveRequest);
                }
                catch (fallbackError) {
                    const secondary = asProviderError(fallbackError, 'token re-resolution');
                    throw new RtcError(secondary.code, `${renewalError.message}; re-resolution also failed: ${secondary.message}`, { cause: fallbackError, retryable: renewalError.retryable && secondary.retryable });
                }
            }
        }
        return this.getRTCTokenInfo(effectiveRequest);
    }
    /** True when at least one uid-mapping provider is configured (independent of tokenInfo). */
    hasUidMapping() {
        return this.uidMappings.length > 0;
    }
    /** `contracts/rtc-provider.json#getUserIdsWithRTCUids` equivalent; partial results are valid. */
    async getUserIdsWithRTCUids(rtcUids, channelName) {
        const unique = [...new Set(rtcUids)];
        if (unique.length > 200) {
            throw rtcError('RTC_UID_MAPPING_INVALID_REQUEST', 'at most 200 rtcUids may be resolved per call');
        }
        for (const uid of unique) {
            if (!Number.isInteger(uid) || uid < 0 || uid > 2147483647) {
                throw rtcError('RTC_UID_MAPPING_INVALID_REQUEST', `invalid rtcUid: ${String(uid)}`);
            }
        }
        if (unique.length === 0)
            return {};
        if (this.uidMappings.length === 0) {
            throw rtcError('RTC_PROVIDER_DISABLED', 'uid mapping is not configured');
        }
        let lastError = null;
        for (const config of this.uidMappings) {
            try {
                return await this.resolveUidsWith(config, unique, channelName);
            }
            catch (error) {
                lastError = asProviderError(error, `${config.type} uid mapping provider`);
            }
        }
        throw (lastError ??
            rtcError('RTC_PROVIDER_UNAVAILABLE', 'no uid mapping provider could resolve the request'));
    }
    async resolveWith(config, request) {
        switch (config.type) {
            case 'static':
                return normalizeTokenInfo(config.token);
            case 'callback': {
                const raw = await config.getRTCTokenInfo(request);
                return normalizeTokenInfo(raw);
            }
            case 'endpoint': {
                if (config.fetchToken) {
                    const raw = await config.fetchToken(request);
                    return normalizeTokenInfo(raw);
                }
                const body = { channelName: request.channelName };
                if (request.uid !== undefined)
                    body.uid = request.uid;
                if (request.userId !== undefined)
                    body.userId = request.userId;
                if (request.role !== undefined)
                    body.role = request.role;
                if (request.ttlSeconds !== undefined)
                    body.ttlSeconds = request.ttlSeconds;
                const raw = await requestJson({
                    fetchImpl: config.fetch ?? this.fetchImpl,
                    url: joinUrl(config.baseUrl, config.path ?? '/v1/tokens'),
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        accept: 'application/json',
                        ...authHeaders(config),
                    },
                    body,
                    timeoutMs: config.timeoutMs ?? 2000,
                    clock: this.clock,
                    label: 'RTCTokenInfo request',
                });
                return normalizeTokenInfo(raw);
            }
            default: {
                const exhaustive = config;
                throw rtcError('invalid_parameter', `unknown token provider type: ${String(exhaustive)}`);
            }
        }
    }
    async resolveUidsWith(config, rtcUids, channelName) {
        switch (config.type) {
            case 'disabled':
                throw rtcError('RTC_PROVIDER_DISABLED', 'uid mapping is disabled by configuration');
            case 'identity-echo': {
                const mapping = {};
                for (const uid of rtcUids)
                    mapping[uid] = String(uid);
                return mapping;
            }
            case 'callback': {
                const raw = await config.getUserIdsWithRTCUids({
                    rtcUids: [...rtcUids],
                    ...(channelName !== undefined ? { channelName } : {}),
                });
                return normalizeUidMapping(raw);
            }
            case 'endpoint': {
                const raw = await requestJson({
                    fetchImpl: config.fetch ?? this.fetchImpl,
                    url: joinUrl(config.baseUrl, config.path ?? '/v1/uids:resolve'),
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        accept: 'application/json',
                        ...authHeaders(config),
                    },
                    body: { rtcUids: [...rtcUids], ...(channelName !== undefined ? { channelName } : {}) },
                    timeoutMs: config.timeoutMs ?? 2000,
                    clock: this.clock,
                    label: 'uid mapping request',
                });
                return normalizeUidMapping(raw);
            }
            default: {
                const exhaustive = config;
                throw rtcError('invalid_parameter', `unknown uid mapping provider type: ${String(exhaustive)}`);
            }
        }
    }
}
function normalizeUidMapping(raw) {
    const outer = readRecord(raw);
    if (!outer)
        throw rtcError('RTC_PROVIDER_UNAVAILABLE', 'uid mapping provider returned no object');
    const record = readRecord(outer.mapping) ?? readRecord(outer.data) ?? outer;
    const mapping = {};
    for (const [key, value] of Object.entries(record)) {
        const uid = Number(key);
        if (!Number.isInteger(uid) || uid < 0 || uid > 2147483647)
            continue;
        if (typeof value !== 'string' || value.length === 0)
            continue;
        mapping[uid] = value;
    }
    return mapping;
}
//# sourceMappingURL=token-provider.js.map